// ADR 10.9.26h §1 — `POST /vocab`, the one transport, and the ladder that
// reads its answer; §3's feedback POST beside it.
//
// **There is deliberately no caller for `POST /vocab/stream` in this file**,
// and AC4 asserts it at the source level. That endpoint is a prose proxy —
// `tier.rs::call_openai` builds the unstructured prompt and `generate.rs`
// relays the provider's raw token deltas — so no frame it sends carries an
// `item_id`, an `image_url`, a `reading` or a `meta.message`. It also sets
// `X-Deprecated` on every response, and neither phone calls it. The first draft
// of this ADR pointed the signed-in arm at it and would have shipped text with
// no thumbs, no images and no save; the Context table records the evidence so
// the next reader does not have to open `tier.rs`.
//
// The wire types are the backend's JSON verbatim (snake_case,
// `crates/core/src/models/evaluate.rs`), the rule `plans.ts` and
// `flashcards.ts` already follow: one declaration per DTO, no renaming layer.
// `item_id`, `reading` and `image_url` are `skip_serializing_if` on the server
// and so **absent** rather than null when unset — the `?` here is the wire's
// truth, and the `?? null` at each read is what feeds the wasm folds, whose
// `Option` arguments want `null` (9.9.26u AC6).

import { VOCAB_GENERATE_TIMEOUT_MS } from '../offline/requestDeadline'
import { authed } from './http'

/** `VocabItem` (`evaluate.rs:105`). `item_id` is present only for pooled items. */
export interface VocabItem {
  item_id?: string
  word: string
  /** Empty for Latin-script targets (29.6.26d) — never absent. */
  pronunciation: string
  reading?: string
  translation: string
  example_sentence: string
  example_translation: string
  example_pronunciation?: string
  translation_pronunciation?: string
  example_translation_pronunciation?: string
  /** Minted per serve for paid/trial learners only (8.7.26o D5). */
  image_url?: string
}

/** `VocabMeta` — `message` is 14.8.26b D6's already-localised sentence. */
export interface VocabMeta {
  level: number
  source_lang: string
  target_lang: string
  framework: string
  count: number
  source: string
  message?: string
}

/** `VocabApiResponse` — the one body `POST /vocab` answers with. */
export interface VocabApiResponse {
  schema_version: number
  items: VocabItem[]
  meta: VocabMeta
  /** 8.6.26b's disclosure block. Carried, never read here. */
  content_meta: Record<string, unknown>
}

/**
 * The `schema_version` this client knows how to render.
 *
 * Every server path sets `1` (`cache.rs:178,204`, `generate.rs:211`). A body
 * carrying any other number is a contract this client has not negotiated, and
 * §1's ladder fails the generation rather than guessing at the item shape.
 */
export const KNOWN_SCHEMA_VERSION = 1

/** `VocabRequest` (`tier.rs:11`). `count` is left to the server's default. */
export interface VocabRequest {
  level: number
  source_lang: string
  target_lang: string
}

/** What one generation hands back: the body, and the headers the meter reads. */
export interface VocabGeneration {
  body: VocabApiResponse
  headers: Headers
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * §1's closed ladder over a 2xx body: a `VocabApiResponse`, or `null`.
 *
 * `null` is **not** an empty list. A body with no `items` array, or a
 * `schema_version` this client does not know, is a server this client does not
 * understand, and rendering it as "no vocabulary" would be the silent success
 * §1 forbids — the caller fails the generation and reports it once (AC2). An
 * empty `items` with a known version is the wall, and passes through with its
 * `meta.message` intact (§3).
 */
export function classifyVocabBody(body: unknown): VocabApiResponse | null {
  if (!isObject(body)) return null
  if (body.schema_version !== KNOWN_SCHEMA_VERSION) return null
  if (!Array.isArray(body.items)) return null
  if (!isObject(body.meta)) return null
  return body as unknown as VocabApiResponse
}

/**
 * `POST /vocab` through `authed` — one request, one body, and the response
 * headers beside it because §2's meter reads `X-RateLimit-*` and `X-Tier` from
 * *this* response and nowhere else.
 *
 * A non-2xx throws `http.ts`'s `ApiError` (the 429 the cap sheet branches on,
 * the 403 a level above the tier's cap answers with server words). A 2xx that
 * is not a `VocabApiResponse` throws too — `classifyVocabBody` is the guard —
 * so the caller never sees a body it cannot render.
 *
 * `anonymous` is ADR 11.9.26i §7's one fork: the same call through `authed`'s
 * stranger arm, which carries `X-Device-Token` instead of a bearer. The caller
 * has awaited `ensureDeviceToken()` first; this function does not, because a
 * transport module that enrols is a transport module with a branch.
 */
export async function generateVocab(
  request: VocabRequest,
  anonymous = false,
  signal?: AbortSignal,
): Promise<VocabGeneration> {
  // ADR 2.10.26s §3 — rust-core's `VOCAB_GENERATE_TIMEOUT_SECS`, not the 20 s default.
  const timeoutMs = VOCAB_GENERATE_TIMEOUT_MS
  const response = await authed('/vocab', { method: 'POST', body: request, anonymous, signal, timeoutMs })
  const raw: unknown = await response.json()
  const body = classifyVocabBody(raw)
  if (body === null) throw new MalformedVocabBody(raw)
  return { body, headers: response.headers }
}

/**
 * A 2xx whose body the ladder rejected (AC2).
 *
 * Carries the top-level keys, sorted, and never a value — `api/tutor.ts`'s
 * `frameSummary` rule: a body embeds generated content, and a diagnostic must
 * not travel with it.
 */
export class MalformedVocabBody extends Error {
  readonly keys: string[]

  constructor(raw: unknown) {
    const keys = isObject(raw) ? Object.keys(raw).sort() : []
    super(`POST /vocab: body is not a VocabApiResponse; keys: [${keys.join(', ')}]`)
    this.name = 'MalformedVocabBody'
    this.keys = keys
  }
}

/** The three targets `feedback.rs` accepts, exactly as it spells them. */
export type VocabFeedbackTarget = 'item' | 'sentence' | 'image'

/**
 * `VocabFeedbackRequest` (`feedback.rs:48`). `rating: null` withdraws — the
 * server deletes the row — which is why the field is nullable rather than
 * optional: the wasm transition table hands back `null` for a withdrawal and
 * the body says so on its face (9.9.26u AC6).
 */
export interface VocabFeedbackBody {
  target: VocabFeedbackTarget
  rating: number | null
  comment?: string
}

/** `POST /vocab/items/{id}/feedback` — 204, so nothing to decode. */
export async function submitVocabFeedback(itemId: string, body: VocabFeedbackBody): Promise<void> {
  await authed(`/vocab/items/${encodeURIComponent(itemId)}/feedback`, { method: 'POST', body })
}
